/* eslint-disable no-param-reassign */
import _ from 'underscore';
import Promise from 'bluebird';
import { getSessionStorage } from '@bingads-webui/storage';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { preferenceDS } from './data-store';
import {
  IPreference, IPreferenceWrite, IUserInfo, IEntityInfo, IPreferenceSyncContext, IStoreOptions,
  IPreferenceLevel,
} from './types';

const Level: Record<string, IPreferenceLevel> = {
  All: 0,
  User: 1,
  Account: 2,
};

/**
 * Creates an independent PreferenceService instance with its own isolated state.
 *
 * The default export is a single shared instance (the historical singleton) - every
 * existing importer keeps that exact object and behaviour. SSR additionally calls this
 * factory to obtain a throwaway, request-local instance for its data prefetch, so the
 * async fetch phase never depends on the shared singleton (see ui-next-ssr render.js).
 * @returns {object} a PreferenceService instance
 */
function createPreferenceService() {
  let isInitialized = false;
  let isReadOnly = false;

  let localRepository: IPreference[] = [];
  let localAccountLevelRepository: IPreference[] = [];
  let localUserLevelRepository: IPreference[] = [];
  let p$preferences: Promise<IPreference[]> | null = null;
  let keySessionStorage: string | null = null;

  const localToRemoteIdMap: Record<string, string> = {};
  const userLevelLocalToRemoteIdMap: Record<string, string> = {};
  let latestLocalId = 0;
  const resolvedPromise: Promise<any> = Promise.resolve();
  let remoteSyncTasks: Promise<any> = resolvedPromise;

  const SessionStorage = getSessionStorage();

  let currentUserInfo: IUserInfo;
  let currentCustomerInfo: IEntityInfo;
  let currentAccountInfo: IEntityInfo;
  let APIHost: string;
  let APIPath: string;
  let oDataToken: string;

  /**
   * Resolves when remote sync tasks is done. Will make use of
   * passed in ID after other optimizations, making it mandatory to pass
   * for that case.
   * @param {String} id - ID for data
   * @returns {Promise} - resolves when remote sync is done
   */
  const onRemoteSyncDone = (id: string): Promise<any> => {
    if (!id) {
      throw new Error('Should pass the ID of the preference item to check');
    }

    return remoteSyncTasks;
  };

  /**
   * Resolves when remote sync tasks for multiple Ids is done. Will make use of
   * passed in IDs after other optimizations, making it mandatory to pass
   * for that case.
   * @param {String[]} ids - IDs for data
   * @returns {Promise} - resolves when remote sync is done
   */
  const onMultipleRemoteSyncDone = (ids: string[]): Promise<any[]> => Promise.all(ids.map((id: string) => onRemoteSyncDone(id)));

  const getStoreOptions = (params?: Record<string, any>, isUserLevel?: boolean): IStoreOptions => {
    const accountId = isUserLevel === true ? 0 : currentAccountInfo.Id;
    const customerId = currentCustomerInfo.Id;

    return {
      cacheResponse: false,
      entitySet: {
        odataURL: `/Customers(${customerId})/Accounts(${accountId})/Preferences`,
        $withKey: function withKey(id: string) {
          const odataURL = `${this.odataURL}(${id})`;
          return {
            odataURL,
          };
        },
      },
      afterUpdate: (resource: any, attrs: any) =>
        // OData Server should return the entity as response for PATCH
        // But it doesn't, so we hack here to use request body as the response
        Promise.resolve(_.defaults({}, params, attrs)),
      host: APIHost,
      path: APIPath,
      oDataToken,
    };
  };

  const concatLocalRepository = (): IPreference[] => {
    if (_.isArray(localUserLevelRepository) && localUserLevelRepository.length > 0) {
      localRepository = localUserLevelRepository.concat(localAccountLevelRepository);
    } else {
      localRepository = localAccountLevelRepository;
    }

    return localRepository;
  };

  const getSessionStorageKey = (): string => {
    const userName = currentUserInfo.Name || 'anonymous';
    const customerId = currentCustomerInfo.Id;

    return `Preferences_${userName}_${customerId}`;
  };

  const parseData = (rawData: string): any => {
    let parsedData = null;

    try {
      parsedData = JSON.parse(rawData);
    } catch (e) {
      parsedData = rawData;
    }

    return parsedData;
  };

  const mapPreferenceData = (rawPreferenceData: any): IPreference[] => _.map(rawPreferenceData, (result: IPreference) => {
    (result).Data = parseData(result.Data); // eslint-disable-line no-param-reassign
    return result;
  });

  const initSync = ({
    initialUserPreferences,
    currentUser,
    currentCustomer,
    currentAccount,
    apiHost,
    apiPath,
    authToken,
    isReadOnly: readOnlyFlag,
  }: IPreferenceSyncContext): IPreference[] => {
    if (localRepository.length === 0) {
      currentUserInfo = currentUser;
      currentCustomerInfo = currentCustomer;
      currentAccountInfo = currentAccount;
      APIHost = apiHost;
      APIPath = apiPath;
      oDataToken = authToken;
      isReadOnly = readOnlyFlag || false;

      const userLevelPreferenceFromPageContext =
        parseData(initialUserPreferences.UserLevelPreferences);
      const accountLevelPreferenceFromPageContext =
        parseData(initialUserPreferences.AccountLevelPreferences);

      if (_.isArray(userLevelPreferenceFromPageContext)
          && _.isArray(accountLevelPreferenceFromPageContext)) {
        localUserLevelRepository = mapPreferenceData(userLevelPreferenceFromPageContext);
        localAccountLevelRepository = mapPreferenceData(accountLevelPreferenceFromPageContext);

        // successfully initialized with data from page context
        isInitialized = true;
        concatLocalRepository();
      }
    }

    return localRepository;
  };

  const initialize = (context: IPreferenceSyncContext): Promise<IPreference[]> => {
    if (!p$preferences) {
      const {
        initialUserPreferences,
        currentUser,
        currentCustomer,
        currentAccount,
        apiHost,
        apiPath,
        authToken,
        isReadOnly: readOnlyFlag,
      } = context;

      const repository = initSync({
        initialUserPreferences,
        currentUser,
        currentCustomer,
        currentAccount,
        apiHost,
        apiPath,
        authToken,
        isReadOnly: readOnlyFlag,
      });

      if (isInitialized) {
        // repository has already been initialized, no need to call findAll()
        p$preferences = Promise.resolve().then(() => repository);
        return p$preferences;
      }

      currentUserInfo = currentUser;
      currentCustomerInfo = currentCustomer;
      currentAccountInfo = currentAccount;
      APIHost = apiHost;
      APIPath = apiPath;
      oDataToken = authToken;
      isReadOnly = readOnlyFlag || false;

      // Use a local settle helper rather than bluebird's Promise.settle:
      // bluebird 3.7.2's stack-frame autodetection returns null in Firefox, so
      // any rejection inside Promise.settle throws "can't access property test, b is null".
      // We don't rely on native Promise.allSettled either, since the runtime
      // Promise here may be bluebird; the helper guarantees a native-shape result.
      type Settled<T> =
        | { status: 'fulfilled'; value: T }
        | { status: 'rejected'; reason: unknown };

      const settle = <T>(p: PromiseLike<T>): Promise<Settled<T>> =>
        Promise.resolve(p).then(
          (value: T): Settled<T> => ({ status: 'fulfilled', value }),
          (reason: unknown): Settled<T> => ({ status: 'rejected', reason })
        );

      p$preferences = Promise.all([
        settle(preferenceDS.findAll({
          limit: Number.MAX_VALUE - 1,
          userLevel: false,
        }, getStoreOptions())),
        settle(preferenceDS.findAll({
          limit: Number.MAX_VALUE - 1,
          userLevel: true,
        }, getStoreOptions({}, true))),
      ]).then((results: [Settled<any>, Settled<any>]) => {
        if (results[1].status === 'fulfilled') {
          const resultUserPreferences = results[1].value;

          if (_.isArray(resultUserPreferences)) {
            localUserLevelRepository = mapPreferenceData(resultUserPreferences);
          }
        }

        if (results[0].status === 'fulfilled') {
          const resultAccountPreferences = results[0].value;

          if (_.isArray(resultAccountPreferences)) {
            localAccountLevelRepository = mapPreferenceData(resultAccountPreferences);
          }
        }

        // successfully initialized with async odata fetch
        isInitialized = true;
        return concatLocalRepository();
      });
    }

    if (!keySessionStorage) {
      keySessionStorage = getSessionStorageKey();
    }

    return p$preferences as Promise<IPreference[]>;
  };

  const findByPrefix = (prefix: string): IPreference[] => {
    if (!prefix) {
      return [];
    }

    const searchPattern = new RegExp(`^${prefix.replace(/\./g, '\\.')}`, 'i');

    return cloneDeep(_.filter(localRepository, (preference: IPreference) => searchPattern.test(preference.Name)));
  };

  const findByName = <TData = IPreference['Data']>(name: string): IPreference<TData> | undefined =>
    cloneDeep(_.findWhere(localRepository, { Name: name }));

  const findByNameAtUserLevel =
    (name: string): IPreference | undefined => cloneDeep(_.findWhere(localUserLevelRepository, { Name: name }));

  const findByNameAtAccountLevel =
    (name: string): IPreference | undefined => cloneDeep(_.findWhere(localAccountLevelRepository, { Name: name }));

  const find = (id: string): IPreference | undefined => cloneDeep(_.findWhere(localRepository, { Id: id }));

  const setToSession = (data: IPreference): Promise<any> => {
    if (!data || !SessionStorage) {
      return Promise.resolve();
    }

    const sessionData: IPreference[] = SessionStorage.get(keySessionStorage!) || [];

    const preference = _.findWhere(sessionData, { Name: data.Name });

    if (_.isEmpty(preference)) {
      sessionData.push(data);
    } else {
      _.extend(preference, data);
    }

    SessionStorage.set(keySessionStorage!, sessionData);

    return Promise.resolve(sessionData);
  };

  const findByNameInSession = (name: string): IPreference | undefined => {
    const sessionData: IPreference[] = SessionStorage.get(keySessionStorage!)!;

    return _.findWhere(sessionData, { Name: name });
  };

  const delFromSession = (name: string): void => {
    if (!name || !SessionStorage) {
      return;
    }

    let sessionData: IPreference[] = SessionStorage.get(keySessionStorage!)!;

    sessionData = _.reject(sessionData as any, { Name: name } as any);
    SessionStorage.set(keySessionStorage!, sessionData);
  };

  /**
   * Generate a local ID for keys created in this window.
   * Once data is persisted to remote, it will be mapped to the
   * remote ID for subsequent syncing.
   * @returns {String} - ID for data
   */
  const generateLocalId = (): string => {
    latestLocalId += 1;
    return `localId_${latestLocalId}`;
  };

  /**
   * Replaces the Id parameter with the remote Id if it
   * was previously mapped
   * @param {Object} params - The params object to save
   * @param {Any} id - The local Id to replace
   * @param {Boolean} isUserLevel - True if it is a user level preference
   * @returns {undefined} - no returns
   */
  const setRemoteId = (params: Record<string, any>, id: string, isUserLevel: boolean): void => {
    if (!_.isString(params.Id) || params.Id.indexOf('localId_') !== 0) {
      return;
    }
    const keyMap = isUserLevel ? userLevelLocalToRemoteIdMap : localToRemoteIdMap;

    params.Id = keyMap[id] || undefined; // eslint-disable-line no-param-reassign
  };

  /**
   * After a preference is destroyed, removes its ID mapping
   * @param {any} id - local ID
   * @param {any} isUserLevel - True if it is a user level preference
   * @returns {undefined} - no returns
   */
  const deleteMappedId = (id: string, isUserLevel: boolean): void => {
    const remoteIdMap = isUserLevel ? userLevelLocalToRemoteIdMap : localToRemoteIdMap;

    delete remoteIdMap[id];
  };

  /**
   * Adds a task for remote syncing to the queue
   * @param {Promise} task - Promise with some CUD operation
   * @returns {Promise} - The promise that will resolve once operation is done
   */
  const addTask = (task: () => Promise<any>): Promise<any> => {
    if (remoteSyncTasks !== resolvedPromise && remoteSyncTasks.isFulfilled()) {
      remoteSyncTasks = resolvedPromise;
    }
    remoteSyncTasks = remoteSyncTasks.then(task).catch(() => {
      // Remote call failed. Chain will continue;
    });
    return remoteSyncTasks;
  };

  const updateLocalCopy = (isUserLevel: boolean, id: string, data: IPreferenceWrite): IPreference => {
    const localCopy = isUserLevel === true ? localUserLevelRepository : localAccountLevelRepository;
    let preference = _.findWhere(localCopy, { Id: id })!;

    if (_.isEmpty(preference)) {
      preference = _.defaults({ Id: id }, data);
      localCopy.push(preference);
    } else {
      _.extend(preference, data);
    }

    concatLocalRepository();

    return cloneDeep(preference);
  };

  const createOrUpdate = (data: IPreferenceWrite, level: IPreferenceLevel): Promise<IPreference | null> => {
    if (!isInitialized) {
      return Promise.resolve(null);
    }

    const isUserLevel = level === Level.User;
    let currentPreference: IPreference | IPreference[] = [];
    if (level === Level.All) {
      currentPreference = findByName(data.Name)!;
    } else if (level === Level.User) {
      currentPreference = findByNameAtUserLevel(data.Name)!;
    } else if (level === Level.Account) {
      currentPreference = findByNameAtAccountLevel(data.Name)!;
    }

    if (currentPreference
        && _.isEqual((currentPreference as IPreference).Data, data.Data)) {
      return Promise.resolve(currentPreference as IPreference);
    }

    if (currentPreference && !data.Id) {
      data.Id = (currentPreference as IPreference).Id; // eslint-disable-line no-param-reassign
    }

    const params = _.defaults({
      // Data is in type String in OData Server
      Data: _.isString(data.Data) ? data.Data : JSON.stringify(data.Data),
    }, _.defaults(data, {
      Type: 'string',
      Version: 1,
    }));

    const id = _.isNull(data.Id) || _.isUndefined(data.Id) ? generateLocalId() : data.Id;
    const preference = updateLocalCopy(isUserLevel, id, data);

    // Skip remote sync if in read-only mode
    if (!isReadOnly) {
      addTask(() => {
        setRemoteId(params, id, isUserLevel);
        return preferenceDS.create(params, getStoreOptions(params, isUserLevel)).then((ret: IPreference) => {
          const remoteIdMap = isUserLevel ? userLevelLocalToRemoteIdMap : localToRemoteIdMap;

          remoteIdMap[id] = ret.Id;
        });
      });
    }

    return Promise.resolve(preference);
  };

  const set = (data: IPreferenceWrite): Promise<IPreference | null> => createOrUpdate(data, Level.All);

  const setAtUserLevel = (data: IPreferenceWrite): Promise<IPreference | null> =>
    createOrUpdate(data, Level.User);

  const setAtAccountLevel = (data: IPreferenceWrite): Promise<IPreference | null> =>
    createOrUpdate(data, Level.Account);

  const processBulkSetResponse = (response: any): { Status?: boolean; Response: any, Action: string } => ({
    Action: 'BatchCreate',
    Response: response,
  });

  const bulkCreate = (params: Record<string, any>, isUserLevel: boolean): Promise<{ Status: boolean; Response: any }> => {
    if (!isInitialized) {
      return Promise.resolve(null) as any;
    }

    // Skip remote calls if in read-only mode
    if (isReadOnly) {
      // Update local copies for all items in the batch
      if (params.reqs && _.isArray(params.reqs)) {
        _.forEach(params.reqs, (data: any) => {
          const id = _.isNull(data.Id) || _.isUndefined(data.Id) ? generateLocalId() : data.Id;
          data.Data = parseData(data.Data);
          updateLocalCopy(isUserLevel, id, data);
        });
        concatLocalRepository();
      }

      return Promise.resolve({
        Status: true,
        Response: [],
      });
    }

    const options = getStoreOptions(params, isUserLevel);

    (options as any).bulkCreate = true;
    (options as any).action = 'POST';
    (options as any).processResponse = processBulkSetResponse;
    (options as any).overrideResponse = true;

    // Make bulkCreate wait for other tasks to finish
    return addTask(() => preferenceDS.create(params, options).then((result: any) => {
      if (!result || !result.Response || !_.isArray(result.Response)) {
        return {
          Status: false,
          Response: result.Response,
        };
      }

      let status = false;

      _.forEach(result.Response, (item: any, index: number) => {
        if (item.status !== 200 || !item.data) {
          return;
        }

        status = true;
        const data = params.reqs[index];

        data.Data = parseData(data.Data);
        updateLocalCopy(isUserLevel, item.data.Id, data);
      });

      concatLocalRepository();

      return {
        Status: status,
        Response: result.Response,
      };
    }));
  };

  const delFromStore = (id: string, isUserLevel: boolean): Promise<any> => {
    const params = { Id: id };

    if (isUserLevel === true) {
      localUserLevelRepository = _.reject(localUserLevelRepository as any, { Id: id } as any);
    } else {
      localAccountLevelRepository = _.reject(localAccountLevelRepository as any, { Id: id } as any);
    }

    concatLocalRepository();

    // Skip remote sync if in read-only mode
    if (!isReadOnly) {
      addTask(() => {
        setRemoteId(params, id, isUserLevel);
        return preferenceDS.destroy(params.Id, getStoreOptions({}, isUserLevel)).then(() => {
          deleteMappedId(id, isUserLevel);
        });
      });
    }

    return Promise.resolve();
  };

  const del = (id: string): Promise<any> => delFromStore(id, false);

  const delAtUserLevel = (id: string): Promise<any> => delFromStore(id, true);

  /**
   * Resets the singleton state so initSync can re-initialize with new data.
   * Used by the SSR server between requests — each request represents a different
   * user with different preferences. In CSR this is never called (single user session).
   * @returns {undefined} - no returns
   */
  const reset = (): void => {
    isInitialized = false;
    isReadOnly = false;
    localRepository = [];
    localAccountLevelRepository = [];
    localUserLevelRepository = [];
    p$preferences = null;
    keySessionStorage = null;

    // const maps — must be cleared in place since callsite closures captured the original reference
    _.each(_.keys(localToRemoteIdMap), (k: string) => { delete localToRemoteIdMap[k]; });
    _.each(_.keys(userLevelLocalToRemoteIdMap), (k: string) => { delete userLevelLocalToRemoteIdMap[k]; });

    latestLocalId = 0;
    remoteSyncTasks = resolvedPromise;
  };

  return {
    get isInitialized() { return isInitialized; },
    initSync,
    initialize,
    reset,
    findByPrefix,
    findByName,
    find,
    set,
    setAtUserLevel,
    setAtAccountLevel,
    bulkCreate,
    del,
    setToSession,
    findByNameInSession,
    delFromSession,
    delAtUserLevel,
    findByNameAtUserLevel,
    findByNameAtAccountLevel,
    onRemoteSyncDone,
    onMultipleRemoteSyncDone,
  };
}

const PreferenceService = createPreferenceService();

export default PreferenceService;
export { createPreferenceService };
